// Lift firmware display helpers for the Lift Details grid.

/**
 * Display value for a lift's firmware: only the part before the first "-".
 * "3.10.1-7a30738 (2026Q3-RC2)" -> "3.10.1". Missing/blank values -> "Unknown".
 */
export function formatFirmwareVersion(value) {
  if (value === null || value === undefined) return "Unknown";
  const version = String(value).split("-")[0].trim();
  return version || "Unknown";
}

/** Roles allowed to edit the latest-version value. Rehrig internal admin only.
 *
 * Deliberately narrower than the Customer Alias edit, which also permits
 * Customer* and Location* roles. An alias is per-lift and visible only to that
 * customer; this is a single global value rendered in the header for every
 * customer, so a customer-side account must not be able to change it.
 *
 * This is the UI affordance only. The PUT endpoint enforces the same rule
 * server-side, which is where it actually matters.
 */
export const FIRMWARE_EDIT_ROLES = ["RpcAdmin"];

/** True when the signed-in role may edit the latest firmware version. */
export function canEditLatestFirmware(userRoleName) {
  return FIRMWARE_EDIT_ROLES.indexOf(userRoleName) !== -1;
}

/**
 * Column header text: "Firmware Version - 3.10.1".
 *
 * Format is verbatim from TE-2007: *Intended display ….. "Firmware Version -
 * 3.10.1"*. An earlier iteration read "Firmware Version (3.10.1 latest)",
 * which spelled out that the number is the target rather than the lift's own
 * version. PBC asked for the dash form, so that is what ships — but note the
 * ambiguity it leaves: the header and the cells now both read as bare version
 * numbers, and a user scanning the column has only position to tell them that
 * the header is the LATEST available and the cells are what each lift reports.
 *
 * Falls back to a plain "Firmware Version" when the value is unset or the
 * fetch failed, so a missing value or a broken call never leaves the column
 * unlabelled. The value is trimmed the same way as the cells, so a pasted
 * Pulsar build string still reads as a bare version in the header.
 */
export function buildFirmwareHeaderLabel(latestVersion) {
  if (latestVersion === null || latestVersion === undefined) {
    return "Firmware Version";
  }
  const trimmed = formatFirmwareVersion(latestVersion);
  if (trimmed === "Unknown") {
    return "Firmware Version";
  }
  return `Firmware Version - ${trimmed}`;
}

/**
 * Client-side mirror of the server's validation, for inline feedback before a
 * pointless round trip. The server remains the authority; this must not be the
 * only check.
 *
 * Returns null when acceptable, otherwise a message to show the editor. An
 * empty value is allowed and clears the header.
 */
export function validateLatestFirmware(value) {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (trimmed.length > 64) {
    return "Keep the version to 64 characters or fewer.";
  }
  if (!/^[A-Za-z0-9][A-Za-z0-9.\-_+()\s]*$/.test(trimmed)) {
    return "Use only letters, digits and . - _ + ( ) characters.";
  }
  if (!/\d+\.\d+/.test(trimmed)) {
    return "Include a version number, for example 3.10.1.";
  }
  return null;
}
